<template>
  <div class="applications w-full min-w-0 max-w-full">
    <OurTabs v-model="selectedTab" class="tabs" size="lg">
      <OurTab
        v-for="item in noticesOptions"
        :key="item.value"
        :label="item.label"
        :value="item.value"
        :disabled="item.disabled"
      />
    </OurTabs>
    <ApplicationCommentsPanel v-if="isCommentsTab" />
    <ApplicationLogsPanel v-else-if="isHistoryTab" />
    <OurGroups v-else>
      <OurGroup v-if="!isSelectedAdd" :title="$t('dealershipApplications.companyDetails')">
        <template #right>
          <div class="flex w-full items-center justify-end gap-2">
            <div
              v-if="companyVerificationTags.length"
              class="verification-error-tags flex items-center gap-2"
            >
              <OurTag
                v-for="tag in companyVerificationTags"
                :key="tag.code"
                :text="tag.text"
                color="red"
              />
            </div>
            <OurTag
              v-if="form.youcontrolSynced"
              :text="$t(`dealershipApplications.synchronizedWith1C`)"
              color="green"
            />
          </div>
        </template>
        <div class="company-details-fields">
          <OurInput
            v-model="form.companyName"
            :label="$t('label.companyShortTitle')"
            :placeholder="$t('placeholder.title')"
            :error="fieldError('companyName')"
            :disabled="viewOnly || form.youcontrolSynced"
          />

          <OurInput
            :value="form.youcontrolSynced ? form.companyFullName : form.companyName"
            :label="$t('label.companyFullTitle')"
            :placeholder="$t('placeholder.title')"
            disabled
            @input="form.companyFullName = $event"
          />

          <OurInput
            :value="form.youcontrolSynced ? form.companyAccountingName : form.companyName"
            :label="$t('label.accountingCompanyName')"
            :placeholder="$t('placeholder.title')"
            disabled
            @input="form.companyAccountingName = $event"
          />

          <OurPhoneInput
            v-model="form.companyPhone"
            :label="$t('label.phoneNumber')"
            :placeholder="$t('placeholder.phone')"
            :flags="false"
            dropdown-disabled
            disabled
          />

          <OurInput
            v-model="form.legalAddress"
            :label="$t('label.companyAddress')"
            :placeholder="$t('placeholder.address')"
            disabled
          />

          <OurSelect
            v-model="form.companyType"
            :options="types"
            :label="$t('label.companyType')"
            :placeholder="$t('placeholder.companyType')"
            :disabled="true"
          />

          <OurInput
            v-if="form.companyType === 'naturalPerson' || form.companyType === 'juridicalPerson'"
            v-model="form.companyCode"
            :label="$t('label.taxNumber')"
            :placeholder="$t('placeholder.taxNumber')"
            :disabled="viewOnly || form.youcontrolSynced"
            :error="fieldError('companyCode')"
          />

          <OurInput
            v-if="form.companyType === 'juridicalPerson'"
            v-model="form.taxNumber"
            :label="$t('label.personalTaxNumber')"
            :placeholder="$t('placeholder.personalTaxNumber')"
            :error="fieldError('taxNumber')"
            :disabled="viewOnly || form.youcontrolSynced"
          />

          <div ref="regionSelectWrapper" class="region-select-wrapper">
            <OurSelect
              v-model="selectedRegionId"
              class="flex-1"
              :options="regions"
              :label="$t('label.region')"
              :placeholder="$t('placeholder.selectRegion')"
              :error="fieldError('regionId')"
              item-label="name"
              :disabled="isRegionSelectDisabled"
            />

            <div v-if="isShownChangeRegionSelect && !viewOnly" class="mt-2 flex gap-2">
              <OurButton class="button" :text="$t('button.change')" @click="onChangeRegion" />

              <OurButton
                class="button"
                variant="secondary"
                :text="$t('button.cancel')"
                @click="onCancelChangeRegion"
              />
            </div>
          </div>

          <OurInput
            v-model="form.companyEmail"
            :label="$t('label.email')"
            :placeholder="$t('placeholder.email')"
            disabled
          />

          <div ref="managerSelectWrapper" class="manager-select-wrapper">
            <OurSelect
              v-model="selectedManagerId"
              :options="managers"
              :label="$t('label.manager')"
              :placeholder="$t('placeholder.manager')"
              :error="fieldError('managerId')"
              :disabled="isManagerSelectDisabled"
            />

            <div v-if="isShownChangeManagerSelect && !viewOnly" class="mt-2 flex gap-2">
              <OurButton class="button" :text="$t('button.change')" @click="onChangeManager" />

              <OurButton
                class="button"
                variant="secondary"
                :text="$t('button.cancel')"
                @click="onCancelChangeManager"
              />
            </div>
          </div>
          <OurSelect
            v-model="form.processingManagerId"
            :options="managers"
            :label="$t('label.processManager')"
            :placeholder="$t('placeholder.manager')"
            :error="fieldError('processingManagerId')"
            :disabled="form.sentToOneC || viewOnly"
          />

          <div class="flex items-center gap-2">
            <OurButton
              v-if="!form.sentToOneC && !viewOnly"
              :loading="sentToOneCLoading"
              class="button"
              :text="$t('button.sendtoOneC')"
              @click.prevent="sendToOneC"
            />
          </div>
        </div>
        <OurTag
          v-for="error in form.creationErrors"
          :key="error.code"
          :text="error.message"
          color="red"
        />
      </OurGroup>
      <OurGroup
        v-if="!isSelectedAdd"
        :title="$t('dealershipApplications.dealerDetails')"
        :loading="!!!companies.length"
      >
        <div class="company-details-fields">
          <OurInput
            v-model="form.name"
            :label="$t('label.title')"
            :placeholder="$t('placeholder.title')"
            :error="fieldError('name')"
            :disabled="viewOnly"
          />

          <OurSelect
            v-model="form.levelId"
            :options="bonusLevels"
            :label="$t('label.bonusLevel')"
            :placeholder="$t('placeholder.bonusLevel')"
            item-label="name"
            :error="fieldError('levelId')"
            :disabled="viewOnly || !getPermission['bonus_program.set_level']"
          />

          <OurSelect
            v-model="form.accessGroupId"
            :options="accessGroups"
            :label="$t('label.accessGroup')"
            :placeholder="$t('placeholder.chooseFromList')"
            :error="fieldError('accessGroupId')"
            :disabled="viewOnly"
          />

          <OurSelect
            v-model="form.storageId"
            :options="storages"
            :label="$t('label.storage')"
            :placeholder="$t('placeholder.selectStorage')"
            item-label="name"
            :error="fieldError('storageId')"
            :disabled="viewOnly"
          />

          <div v-if="companies.length">
            <OurMultiselect
              v-model="form.companies"
              class="mb-2 min-w-0"
              :options="companies"
              :label="$t('label.company') + ' *'"
              :placeholder="$t('placeholder.selectCompany')"
              item-label="label"
              :error="fieldError('companies')"
              :reload="!viewOnly"
              :disabled="viewOnly"
              :loading="loading"
              @reloadData="reload"
            />

            <p class="dealer-company-hint text-sm text-gray-500">
              * Перша вибрана компанія буде компанією за замовчуванням
            </p>
          </div>
        </div>
      </OurGroup>
      <OurGroup :title="$t('dealershipApplications.userDetails')">
        <template #right>
          <div
            v-if="userVerificationTags.length"
            class="verification-error-tags flex items-center gap-2"
          >
            <OurTag
              v-for="tag in userVerificationTags"
              :key="tag.code"
              :text="tag.text"
              color="red"
            />
          </div>
        </template>
        <div class="company-details-fields">
          <PaginationSelect
            v-if="isSelectedAdd"
            v-model="form.customerId"
            class="full-width"
            :options="customers"
            :options-limit="customers?.length"
            :label="$t('label.customer')"
            :placeholder="$t('placeholder.selectCustomer')"
            :error="fieldError('customerId')"
            :disabled="viewOnly"
          />

          <OurInput
            v-model="form.lastName"
            :label="$t('label.lastName')"
            :placeholder="$t('placeholder.lastName')"
            :disabled="true"
          />

          <OurInput
            v-model="form.firstName"
            :label="$t('label.name')"
            :placeholder="$t('placeholder.name')"
            :disabled="true"
          />

          <OurSelect
            v-model="form.roleId"
            :options="roles"
            :label="$t('label.role')"
            :placeholder="$t('placeholder.selectRole')"
            :error="fieldError('roleId')"
            disabled
          />

          <OurInput
            v-model="form.email"
            data-cy="email-input"
            :label="$t('label.email')"
            :placeholder="$t('placeholder.email')"
            :disabled="true"
          />

          <OurPhoneInput
            v-model="form.phone"
            :label="$t('label.phoneNumber')"
            :placeholder="$t('placeholder.phone')"
            :flags="false"
            dropdown-disabled
            :disabled="true"
          />

          <OurTextarea
            v-if="!editForm"
            v-model="form.comment"
            :label="$t('label.comment')"
            :placeholder="$t('placeholder.comment')"
            rows="4"
            :disabled="viewOnly"
          />
        </div>
      </OurGroup>
      <OurGroup
        v-if="editForm && selectedTab === 'addNew'"
        :title="$t('dealershipApplications.additionalInformation')"
      >
        <div class="additional-information-grid">
          <div class="additional-information-grid__cell">
            <OurInput
              v-model="form.mainActivityDirection"
              :label="$t('page.registration.fieldMainActivity')"
              :placeholder="$t('placeholder.fieldMainActivity')"
              disabled
            />
          </div>
          <div class="additional-information-grid__cell additional-information-grid__cell--full">
            <OurInput
              v-model="form.mainClients"
              label-multiline
              :label="$t('page.registration.fieldPrimaryClients')"
              :placeholder="$t('placeholder.fieldPrimaryClients')"
              disabled
            />
          </div>
          <div class="additional-information-grid__cell">
            <OurInput
              v-model="form.employeesTotal"
              :label="$t('page.registration.fieldEmployeesTotal')"
              :placeholder="$t('placeholder.fieldEmployeesTotal')"
              disabled
            />
          </div>
          <div class="additional-information-grid__cell">
            <OurInput
              v-model="form.employeesInstallers"
              :label="$t('page.registration.fieldEmployeesInstallers')"
              :placeholder="$t('placeholder.fieldEmployeesInstallers')"
              disabled
            />
          </div>
          <div class="additional-information-grid__cell">
            <OurInput
              v-model="form.employeesEngineers"
              :label="$t('page.registration.fieldEmployeesEngineers')"
              :placeholder="$t('placeholder.fieldEmployeesEngineers')"
              disabled
            />
          </div>
          <div class="additional-information-grid__cell">
            <OurInput
              v-model="form.employeesCommercial"
              :label="$t('page.registration.fieldEmployeesCommercial')"
              :placeholder="$t('placeholder.fieldEmployeesCommercial')"
              disabled
            />
          </div>
          <div class="additional-information-grid__cell">
            <OurInput
              v-model="form.companyYears"
              :label="$t('page.registration.fieldCompanyYears')"
              :placeholder="$t('placeholder.fieldCompanyYears')"
              disabled
            />
          </div>
          <div class="additional-information-grid__cell additional-information-grid__cell--full">
            <OurInput
              v-model="form.dealerBrands"
              label-multiline
              :label="$t('page.registration.fieldDealerBrands')"
              :placeholder="$t('placeholder.fieldDealerBrands')"
              disabled
            />
          </div>

          <div class="additional-information-grid__cell">
            <OurInput
              v-model="form.trainingMountingService"
              :label="$t('page.registration.fieldTrainingMounting')"
              :placeholder="$t('placeholder.fieldTrainingMounting')"
              disabled
            />
          </div>
          <div class="additional-information-grid__cell">
            <OurInput
              v-model="form.trainingCalculationsDesign"
              :label="$t('page.registration.fieldTrainingDesign')"
              :placeholder="$t('placeholder.fieldTrainingDesign')"
              disabled
            />
          </div>
          <div class="additional-information-grid__cell">
            <OurInput
              v-model="form.trainingSalesPromotion"
              :label="$t('page.registration.fieldTrainingSales')"
              :placeholder="$t('placeholder.fieldTrainingSales')"
              disabled
            />
          </div>

          <div class="additional-information-grid__cell">
            <OurInput
              v-model="form.plannedAtmosferaVolumeUsd"
              label-multiline
              :label="$t('page.registration.fieldPlannedVolumeUsd')"
              :placeholder="$t('placeholder.fieldPlannedVolumeUsd')"
              disabled
            />
          </div>
          <div class="additional-information-grid__cell">
            <OurInput
              v-model="form.employeesRenewableEnergy"
              label-multiline
              :label="$t('page.registration.fieldRenewableEmployees')"
              :placeholder="$t('placeholder.fieldRenewableEmployees')"
              disabled
            />
          </div>

          <div class="additional-information-grid__cell">
            <OurTextarea
              v-model="form.comment"
              :label="$t('label.comment')"
              :placeholder="$t('placeholder.comment')"
              rows="4"
              disabled
            />
          </div>
          <div class="additional-information-grid__cell">
            <OurInput
              :value="form.heardAboutSource ? $t(`page.registration.${form.heardAboutSource}`) : ''"
              :label="$t('page.registration.fieldHeardAboutSource')"
              :placeholder="$t('placeholder.fieldHeardAboutSource')"
              disabled
            />
          </div>
          <div v-if="form.heardAboutSource === 'other'" class="additional-information-grid__cell">
            <OurTextarea
              v-model="form.heardAboutSourceOther"
              :label="$t('page.registration.fieldHeardAboutSourceOtherField')"
              :placeholder="$t('placeholder.fieldHeardAboutSourceOther')"
              rows="4"
              disabled
            />
          </div>
        </div>
      </OurGroup>
    </OurGroups>
  </div>
</template>

<script>
import ValidationService from "@/services/validation.service";
import { email, maxLength, minLength, numeric, requiredIf } from "vuelidate/lib/validators";
import { mapGetters, mapMutations, mapState } from "vuex";
import apolloMixin from "@/mixins/apollo.mixin";
import PaginationSelect from "@/ui/select/PaginationSelect.vue";
import ApplicationCommentsPanel from "@/views/settings/dealership-applications/_components/ApplicationCommentsPanel.vue";
import ApplicationLogsPanel from "@/views/settings/dealership-applications/_components/ApplicationLogsPanel.vue";
import { allCustomersQuery } from "@/views/settings/customers/index/gql/queries";

const { TAX_NUMBER_COMPANY_MAX_LENGTH, TAX_NUMBER_MIN_LENGTH, TEXT_MAX_LENGTH } = ValidationService;

const VERIFICATION_ERROR_SECTIONS = {
  company: ["company_duplicate", "youcontrol_no_data", "edrpou_empty"],
  user: ["email_duplicate", "phone_duplicate"],
};

export default {
  name: "DealershipApplicationsForm",
  components: { PaginationSelect, ApplicationCommentsPanel, ApplicationLogsPanel },

  mixins: [apolloMixin],

  props: {
    value: {
      type: Object,
      default: () => {},
    },

    editForm: {
      type: Boolean,
      default: false,
    },

    userDeleted: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    sentToOneCLoading: {
      type: Boolean,
      default: false,
    },

    regions: {
      type: Array,
      default: () => [],
    },

    bonusLevels: {
      type: Array,
      default: () => [],
    },

    isShownChangeRegionSelect: {
      type: Boolean,
      default: false,
    },

    isShownChangeManagerSelect: {
      type: Boolean,
      default: false,
    },

    viewOnly: {
      type: Boolean,
      default: false,
    },

    draftRegionId: {
      type: [String, Number],
      default: "",
    },

    draftManagerId: {
      type: [String, Number],
      default: "",
    },

    status: {
      type: [Object, String],
      default: () => {},
    },
  },

  data() {
    return {
      form: {
        companyName: "",
        companyFullName: "",
        companyAccountingName: "",
        companyPhone: "",
        legalAddress: "",
        companyType: "",
        companyCode: "",
        companyEmail: "",
        youcontrolSynced: false,
        sentToOneC: false,
        verificationErrors: [],
        creationErrors: [],
        verifiedAt: null,
        name: "",
        taxNumber: "",
        lastName: "",
        firstName: "",
        regionId: "",
        managerId: "",
        processingManagerId: "",
        roleId: "",
        email: "",
        phone: "",
        comment: "",
        mainActivityDirection: "",
        mainClients: "",
        employeesTotal: "",
        employeesInstallers: "",
        employeesEngineers: "",
        employeesCommercial: "",
        companyYears: "",
        dealerBrands: "",
        trainingMountingService: "",
        trainingCalculationsDesign: "",
        trainingSalesPromotion: "",
        plannedAtmosferaVolumeUsd: "",
        employeesRenewableEnergy: "",
        storageId: "",
        accessGroupId: "",
        levelId: "1",
        companies: [],
        customerId: "",
        heardAboutSource: "",
        heardAboutSourceOther: "",
      },
      types: [
        {
          id: "naturalPerson",
          label: "Фізична особа-підприємець",
        },
        {
          id: "juridicalPerson",
          label: "Юридична особа",
        },
      ],
      selectedTab: "addNew",
    };
  },

  validations() {
    const requiredOnMainTab = requiredIf(function () {
      return !this.isSelectedAdd;
    });
    const requiredOnAddTab = requiredIf(function () {
      return this.isSelectedAdd;
    });

    return {
      form: {
        companyName: { required: requiredOnMainTab },
        regionId: { required: requiredOnMainTab },
        managerId: { required: requiredOnMainTab },
        processingManagerId: { required: requiredOnMainTab },
        name: { required: requiredOnMainTab },
        taxNumber: {
          numeric,
          minLength: minLength(TAX_NUMBER_MIN_LENGTH),
          maxLength: maxLength(TAX_NUMBER_COMPANY_MAX_LENGTH),
        },
        companyCode: {
          required: requiredOnMainTab,
        },
        firstName: {
          required: requiredOnMainTab,
          maxLength: maxLength(TEXT_MAX_LENGTH),
        },
        lastName: {
          required: requiredOnMainTab,
          maxLength: maxLength(TEXT_MAX_LENGTH),
        },
        email: {
          required: requiredOnMainTab,
          email,
        },
        roleId: { required: requiredOnMainTab },
        storageId: { required: requiredOnMainTab },
        accessGroupId: { required: requiredOnMainTab },
        companies: { required: requiredOnMainTab },
        levelId: { required: requiredOnMainTab },
        customerId: { required: requiredOnAddTab },
      },
    };
  },

  computed: {
    ...mapState("dealerUsers", ["roles", "companies", "customers"]),
    ...mapState("dealerCompany", ["managers", "accessGroups"]),
    ...mapState("accessGroups", ["storages"]),
    ...mapGetters("user", ["getPermission"]),

    noticesOptions() {
      const options = [
        { value: "addNew", label: this.$t("label.createNew") },
        { value: "comments", label: this.$t("title.dealershipApplications.comments") },
        { value: "history", label: this.$t("title.dealershipApplications.history") },
      ];

      if (this.status?.value && this.status?.value !== "approved") {
        options.splice(1, 0, { value: "add", label: this.$t("label.addOneMore") });
      }

      return options;
    },

    isSelectedAdd() {
      return this.selectedTab === "add";
    },

    isCommentsTab() {
      return this.selectedTab === "comments";
    },

    isHistoryTab() {
      return this.selectedTab === "history";
    },

    companyVerificationTags() {
      return this.getVerificationTags(VERIFICATION_ERROR_SECTIONS.company);
    },

    userVerificationTags() {
      return this.getVerificationTags(VERIFICATION_ERROR_SECTIONS.user);
    },

    isRegionSelectDisabled() {
      return this.viewOnly || !this.isShownChangeRegionSelect;
    },

    isManagerSelectDisabled() {
      return this.viewOnly || !this.isShownChangeManagerSelect;
    },

    selectedRegionId: {
      get() {
        return this.isShownChangeRegionSelect ? this.draftRegionId : this.form.regionId;
      },
      set(value) {
        this.form.regionId = value;
        this.$emit("update-draft-region-id", value);
      },
    },

    selectedManagerId: {
      get() {
        return this.isShownChangeManagerSelect ? this.draftManagerId : this.form.managerId;
      },
      set(value) {
        this.form.managerId = value;
        this.$emit("update-draft-manager-id", value);
      },
    },
  },

  watch: {
    value: {
      handler: "getForm",
      deep: true,
    },

    selectedTab: {
      handler: function (val) {
        this.$v.$reset();
        this.$emit("tab-change", val);
      },
    },
    isShownChangeRegionSelect(value) {
      if (!value) {
        this.$v.form.regionId.$reset();
      }
    },
    isShownChangeManagerSelect(value) {
      if (!value) {
        this.$v.form.managerId.$reset();
      }
    },
    draftRegionId(value) {
      this.form.regionId = value;
    },
    draftManagerId(value) {
      this.form.managerId = value;
    },
  },

  mounted() {
    this.getForm();
    this.getAllCustomers();
  },

  methods: {
    ...mapMutations("dealerUsers", ["SET_CUSTOMERS"]),

    fieldError(field) {
      const validation = this.$v.form[field];
      const minLengthByField = {
        taxNumber: TAX_NUMBER_MIN_LENGTH,
      };
      const maxLengthByField = {
        taxNumber: TAX_NUMBER_COMPANY_MAX_LENGTH,
        firstName: TEXT_MAX_LENGTH,
        lastName: TEXT_MAX_LENGTH,
      };

      if (!validation?.$dirty || !validation.$invalid) {
        return "";
      }

      if (validation.required === false) {
        return this.$t("validation.required");
      }

      if (validation.numeric === false) {
        return this.$t("validation.taxNumber");
      }

      if (validation.minLength === false) {
        return this.$t("validation.textMinLength", {
          length: minLengthByField[field],
        });
      }

      if (validation.maxLength === false) {
        return this.$t("validation.textMaxLength", {
          length: maxLengthByField[field],
        });
      }

      if (validation.email === false) {
        return this.$t("validation.mustBeEmail");
      }

      return this.$t("validation.required");
    },

    getVerificationTagText(code, errors = this.form.verificationErrors ?? []) {
      const key = `dealershipApplications.${code}`;

      if (this.$te(key)) {
        return this.$t(key);
      }

      const error = errors.find((item) => item.code === code);

      return error?.message ?? code;
    },

    getVerificationTags(codes) {
      const errors = this.form.verificationErrors ?? [];

      return codes
        .filter((code) => errors.some((error) => error.code === code))
        .map((code) => ({
          code,
          text: this.getVerificationTagText(code, errors),
        }));
    },

    async getAllCustomers() {
      const { allCustomer } = await this.$get(allCustomersQuery);

      this.SET_CUSTOMERS(allCustomer);
    },

    getForm() {
      if (this.value) {
        this.form = { ...this.value };
      }
    },

    sendToOneC() {
      if (
        this.$v.form.companyName.$invalid ||
        this.$v.form.regionId.$invalid ||
        this.$v.form.managerId.$invalid ||
        this.$v.form.processingManagerId.$invalid ||
        this.$v.form.companyCode.$invalid
      ) {
        this.$v.form.companyName.$touch();
        this.$v.form.regionId.$touch();
        this.$v.form.managerId.$touch();
        this.$v.form.processingManagerId.$touch();
        this.$v.form.companyCode.$touch();

        return;
      }

      this.$emit("sendToOneC", {
        companyName: this.form.companyName,
        regionId: this.form.regionId,
        managerId: this.form.managerId,
        processingManagerId: this.form.processingManagerId,
        taxNumber: this.form.taxNumber,
        companyCode: this.form.companyCode,
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        phone: this.form.phone,
        email: this.form.email,
      });
    },

    reload() {
      this.$emit("reload");
    },

    onChangeRegion() {
      this.$emit("changeRegion");
    },

    onCancelChangeRegion() {
      this.$emit("cancelChangeRegion");
    },

    onChangeManager() {
      this.$emit("changeManager");
    },

    onCancelChangeManager() {
      this.$emit("cancelChangeManager");
    },

    emitSingleFieldChangeForm(field) {
      if (this.selectedTab === "history" || this.selectedTab === "comments") {
        this.selectedTab = "addNew";
      }

      this.$v.form[field].$touch();

      if (this.$v.form[field].$invalid) {
        this.form.isValid = false;
        this.$emit("input", this.form);
        this.$nextTick(() => {
          const error = document.querySelector(".error");

          if (error) {
            error.scrollIntoView({ behavior: "instant", block: "end" });
          } else {
            const errorInput = document.querySelector(".error-input");

            if (errorInput) {
              errorInput.scrollIntoView({ behavior: "instant", block: "end" });
            }
          }
        });

        return false;
      }

      this.form.isValid = true;
      this.$emit("input", this.form);

      return true;
    },

    emitRegionChangeForm() {
      return this.emitSingleFieldChangeForm("regionId");
    },

    emitManagerChangeForm() {
      return this.emitSingleFieldChangeForm("managerId");
    },

    emitSyncWith1CForm() {
      return this.emitSingleFieldChangeForm("companyName");
    },

    scrollToVerificationErrors() {
      this.$nextTick(() => {
        const verificationTag = this.$el.querySelector(".verification-error-tags .tag");

        verificationTag?.scrollIntoView({ behavior: "instant", block: "center" });
      });
    },

    emitForm() {
      const submit = () => {
        if (this.$v.$invalid) {
          this.$v.$touch();

          this.$nextTick(() => {
            const error = document.querySelector(".error");

            if (error) {
              error.scrollIntoView({ behavior: "instant", block: "end" });
            } else {
              const errorInput = document.querySelector(".error-input");

              if (errorInput) {
                errorInput.scrollIntoView({ behavior: "instant", block: "end" });
              }
            }
          });

          return;
        }

        this.form.isValid = true;
        this.$emit("input", this.form);
      };

      if (this.selectedTab === "history" || this.selectedTab === "comments") {
        this.selectedTab = "addNew";
        this.$nextTick(submit);

        return;
      }

      submit();
    },

    emitSaveForm() {
      this.$emit("input", this.form);
    },

    scrollToRegionSelect() {
      this.$refs.regionSelectWrapper.scrollIntoView({ behavior: "smooth", block: "center" });
    },

    scrollToManagerSelect() {
      this.$refs.managerSelectWrapper.scrollIntoView({ behavior: "smooth", block: "center" });
    },
  },
};
</script>

<style scoped lang="postcss">
.applications:deep {
  .our-divider-wrapper {
    display: none;
  }

  .company-details-fields {
    @apply grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3;
    grid-auto-flow: row dense;
  }

  .additional-information-grid {
    @apply grid grid-cols-1 gap-4 sm:gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-3;
    grid-auto-flow: row;
    align-items: stretch;
  }

  .additional-information-grid__cell {
    @apply min-w-0;
  }

  .additional-information-grid__cell--full {
    grid-column: 1 / -1;
  }

  .additional-information-grid__intro {
    @apply mb-0 text-sm text-gray-600;
  }
}
</style>
